import type {
  IValidateProject,
  IValidField,
  TValidationStatus,
} from '@/enteties/config/model';
import {
  validateProjectGet,
  validateProjectPost,
} from '@/enteties/project/api';
import { useQueryWithOnError } from '@/shared/hooks/query/useQueryWithOnError';
import { useMutation } from '@tanstack/react-query';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import * as m from '@paraglide/messages';
import parse from 'html-react-parser';

interface IErrorForField {
  [key: string]: {
    [key: string]: {
      [key in TValidationStatus]: string;
    };
  };
}

const errorForField: IErrorForField = {
  python: {
    requirementsPath: {
      // NO_FILE: m.tab_config_python_requirementsPath_error_noFile(),
      NO_FILE: parse(m.tab_config_python_requirementsPath_error_wrongName()),
      WRONG_PATH: m.tab_config_python_requirementsPath_error_wrongPath(),
    },
    scriptName: {
      NO_FILE: m.tab_config_mainFileName_error_wrongName(),
      WRONG_PATH: m.tab_config_mainFileName_error_wrongPath(),
    },
    command: {
      NO_FILE: m.tab_config_mainFileName_error_wrongName(),
      WRONG_PATH: m.tab_config_mainFileName_error_wrongPath(),
    },
  },
  jvm: {
    jarName: {
      NO_FILE: m.tab_config_mainFileName_error_wrongName(),
      WRONG_PATH: m.tab_config_mainFileName_error_wrongPath(),
    },
  },
  node: {
    scriptName: {
      NO_FILE: m.tab_config_mainFileName_error_wrongName(),
      WRONG_PATH: m.tab_config_mainFileName_error_wrongPath(),
    },
  },
  csharp: {
    mainFile: {
      NO_FILE: m.tab_config_mainFileName_error_wrongName(),
      WRONG_PATH: m.tab_config_mainFileName_error_wrongPath(),
    },
    buildFileName: {
      NO_FILE: m.tab_config_mainFileName_error_wrongName(),
      WRONG_PATH: m.tab_config_mainFileName_error_wrongPath(),
    },
  },
  ruby: {
    mainScript: {
      NO_FILE: m.tab_config_mainFileName_error_wrongName(),
      WRONG_PATH: m.tab_config_mainFileName_error_wrongPath(),
    },
  },
};

// const errorMessage = (
//   suggestion: IValidField,
//   environment: string,
//   fieldValue: string,
//   fieldName: string
// ) => {
//   let errorType;

//   if (!suggestion.suggestions.length) {
//     errorType = 'noFile';
//   } else if (
//     suggestion.suggestions[0]?.path &&
//     fieldValue !== suggestion.suggestions[0]?.path
//   ) {
//     errorType = 'wrongPath';
//   } else if (
//     suggestion.suggestions[0]?.name &&
//     fieldValue !== suggestion.suggestions[0]?.name
//   ) {
//     errorType = 'wrongName';
//   }

//   return (
//     errorForField?.[environment]?.[fieldName]?.[errorType] ??
//     'Значение не совпадает'
//   );
// };

export const useValidateConfig = (slug: string, enabled: boolean) => {
  const form = useFormContext();
  const [suggestions, setSuggestions] = useState<
    IValidateProject | undefined
  >();
  const { data: validateData, isSuccess: validateSuccess } =
    useQueryWithOnError({
      ...validateProjectGet.validateProjectQueryOptions({ slug }),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      enabled,
    });

  const setErrors = useCallback(
    (validData: IValidateProject) => {
      const config = form.getValues();
      const environment = form.getValues('meta.environment');
      // Use lodash to flatten nested objects and set form errors
      _.forOwn(config, (sectionData, sectionName) => {
        // Ensure sectionData is an object
        if (typeof sectionData === 'object' && sectionData !== null) {
          // Iterate over each field in the section
          _.forOwn(sectionData, (value, fieldName) => {
            // debugger;
            // If a suggestion exists for this field, set an error
            const suggestionMessage: IValidField | undefined = _.get(
              validData,
              [sectionName, fieldName]
            );
            if (suggestionMessage && !suggestionMessage?.isValid) {
              const errorStatus = suggestionMessage.validationStatus;
              // Set an error in React Hook Form with the full path to the field
              form.setError(`${sectionName}.${fieldName}` as any, {
                type: 'manual',
                message:
                  errorForField?.[environment]?.[fieldName]?.[errorStatus],
              });
            }
          });
        }
      });
      // console.log('new errors set');
    },
    [form]
  );

  useEffect(() => {
    if (validateSuccess) {
      setSuggestions(validateData);
      setErrors(validateData);
    }
  }, [validateSuccess, validateData]);

  const { mutate: mutateValidate } = useMutation({
    ...validateProjectPost.validateProjectMutationOptions({ slug }),
    onSuccess: (data) => {
      setSuggestions(data);
      setErrors(data);
    },
  });

  return { suggestions, mutateValidate, validateSuccess };
};
